import React from 'react';
import { Form } from 'antd';
import { ExclamationCircleOutlined, CheckCircleTwoTone } from '@ant-design/icons';
import PropTypes from 'prop-types';
import { LocalizationContext } from '../../../hoc/Localization';
import { RouteContext } from '../../../hoc/Route';
import Button from '../../Button';
import Input from '../../Input';
import api from '../../../utils/api';
import { getCookie } from '../../../utils/cookies';
import styles from './styles.less';

const FormItem = Form.Item;

const SubscribeComponent = ({
    course,
    onSubmit,
    user,
    subscribeButtonType,
    closeModal,
    subscribeName,
    pageSuffix,
}) => {
    const { t } = React.useContext(LocalizationContext);
    const [loading, setLoading] = React.useState(false);
    const [status, setStatus] = React.useState(null);
    const [form] = Form.useForm();
    const { page } = React.useContext(RouteContext);

    React.useEffect(() => {
        if (user && user.email) {
            form.setFieldsValue({
                email: user.email,
            });
        }
    }, [user]);

    const statusesMap = {
        success: {
            text: (
                <p className={styles.successMesage}>
                    <CheckCircleTwoTone twoToneColor="#52c41a" data_gh="subscribeSuccess" />{' '}
                    {t('frontpage.newsletter.form.success')}
                </p>
            ),
        },
        missingEmail: (
            <div className={styles.validation}>
                <ExclamationCircleOutlined />{' '}
                {t('frontpage.newsletter.form.validation.requiredEmail')}
            </div>
        ),
        invalidEmail: (
            <div className={styles.validation}>
                <ExclamationCircleOutlined />{' '}
                {t('frontpage.newsletter.form.validation.invalidEmail')}
            </div>
        ),
        existingEmail: {
            text: t('frontpage.newsletter.form.validation.duplicate'),
        },
    };

    const handleSubmit = async values => {
        setStatus({ status: null });
        setLoading(true);
        const response = await api
            .post('user-subscriptions', {
                email: values.email,
                courseShortId: course.shortId,
                page: `${page}${pageSuffix}`,
                categories: [course.category.title || course.category.name],
                userTrackingId: getCookie('sync'),
            })
            .catch(() => null);

        form.resetFields();
        if (response) {
            setStatus(response.type || response.status);
            closeModal(response.status);
        }
        setLoading(false);
        onSubmit();
        setTimeout(() => setStatus({ status: null }), 15000);
        return true;
    };

    return (
        <Form form={form} onFinish={handleSubmit}>
            <FormItem
                style={{
                    marginBottom: 0,
                }}
                name="email"
                rules={[
                    { type: 'email', message: statusesMap.invalidEmail },
                    { required: true, message: statusesMap.missingEmail },
                ]}
                normalize={e => e.trim()}
            >
                {status !== 'success' && (
                    <Input
                        version="long"
                        autoComplete="off"
                        placeholder={t('email.popup.input')}
                        onInput={() => setStatus(null)}
                        className={styles.subscribeEmailInput}
                        size="large"
                        name={`${subscribeName}_input`}
                    />
                )}
            </FormItem>
            {!!status && (
                <div id="subscribeStatus" className={styles.status}>
                    {statusesMap[status] && statusesMap[status].icon}
                    {statusesMap[status] && statusesMap[status].text}
                </div>
            )}
            {status !== 'success' && (
                <Button
                    className={styles.subscribeButton}
                    version="large"
                    type={subscribeButtonType}
                    htmlType="submit"
                    loading={loading}
                    data_gh={`${subscribeName}_button`}
                >
                    {t('email.popup.button')}
                </Button>
            )}
        </Form>
    );
};

SubscribeComponent.propTypes = {
    course: PropTypes.object,
    onSubmit: PropTypes.func,
    user: PropTypes.object,
    subscribeButtonType: PropTypes.string,
    closeModal: PropTypes.func,
    subscribeName: PropTypes.string,
    pageSuffix: PropTypes.string,
};
SubscribeComponent.defaultProps = {
    onSubmit: () => {},
    closeModal: () => {},
    course: {},
    user: {},
    subscribeButtonType: 'secondary',
    subscribeName: '',
    pageSuffix: '',
};

export default SubscribeComponent;
